import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, map, of, switchMap, tap } from 'rxjs';

import {
  AuditAnnualTargetsResponseDto,
  SaveAuditAnnualPlanPayload,
  AuditAssignmentListResponseDto,
  AuditAssignmentDto,
  AuditAnswerAttachmentDto,
  AuditAnswerAutosavePayload,
  AuditAnswerDto,
  AuditCalendarResponseDto,
  AuditDto,
  AuditExecutionDetailDto,
  AuditExecutionPlanDto,
  AuditListResponseDto,
  AuditLocationListResponseDto,
  AuditUnlockInboxDto,
  AuditUnlockRequestDto,
  LocationListResponseDto,
  ReplaceAuditLocationsPayload,
  RoleListResponseDto,
  CreateAuditAssignmentPayload,
  CreateAuditPayload,
  ReplaceAuditAssignmentResponseDto,
  UpdateAuditPayload,
  UserLocationsResponseDto,
} from '../models/audit.models';

@Injectable({ providedIn: 'root' })
export class AuditsApi {
  private readonly http = inject(HttpClient);
  private readonly executionSessions = new Map<string, string>();

  private sessionOptions(planId: string): { headers: Record<string, string> } {
    const session = this.executionSessions.get(planId);
    return { headers: session ? { "X-Audit-Session": session } : {} };
  }

  pauseAuditExecution(planId: string): Observable<void> {
    const options = this.sessionOptions(planId);
    const active = this.executionSessions.delete(planId);
    return active ? this.http.post<void>(`${this.executionPlansBaseUrl}/${planId}/pause/`, {}, { ...options, keepalive: true }) : of(undefined);
  }
  private readonly auditBaseUrl = '/api/bhp/api/audit';
  private readonly auditsBaseUrl = '/api/bhp/api/audits';
  private readonly calendarBaseUrl = '/api/bhp/api/calendar/audits';
  private readonly executionPlansBaseUrl = '/api/bhp/api/audit-execution-plans';
  private readonly locationsBaseUrl = '/api/bhp/api/locations';
  private readonly rolesBaseUrl = '/api/auth/api/roles';

  getAudits(): Observable<AuditListResponseDto> {
    return this.http.get<AuditListResponseDto>(`${this.auditsBaseUrl}/`);
  }

  getAudit(auditId: string): Observable<AuditDto> {
    return this.http.get<AuditDto>(`${this.auditBaseUrl}/${auditId}/`);
  }

  createAudit(payload: CreateAuditPayload): Observable<AuditDto> {
    return this.http.post<AuditDto>(`${this.auditBaseUrl}/`, payload);
  }

  updateAudit(auditId: string, payload: UpdateAuditPayload): Observable<AuditDto> {
    return this.http.patch<AuditDto>(`${this.auditBaseUrl}/${auditId}/`, payload);
  }

  getLocations(): Observable<LocationListResponseDto> {
    return this.http.get<LocationListResponseDto>(`${this.locationsBaseUrl}/`);
  }

  getAuditAvailableLocations(): Observable<LocationListResponseDto> {
    return this.http.get<LocationListResponseDto>(`${this.auditsBaseUrl}/locations/available/`);
  }

  getUserLocations(userCode: string): Observable<UserLocationsResponseDto> {
    return this.http.get<UserLocationsResponseDto>(`/api/bhp/api/userLocations/${userCode}/`);
  }

  getRoles(): Observable<RoleListResponseDto> {
    return this.http.get<RoleListResponseDto>(`${this.rolesBaseUrl}/`);
  }

  getAuditAvailableRoles(): Observable<RoleListResponseDto> {
    return this.http.get<RoleListResponseDto>(`${this.auditsBaseUrl}/roles/available/`);
  }

  getAuditLocations(auditId: string): Observable<AuditLocationListResponseDto> {
    return this.http.get<AuditLocationListResponseDto>(
      `${this.auditBaseUrl}/${auditId}/locations/`,
    );
  }

  replaceAuditLocations(
    auditId: string,
    payload: ReplaceAuditLocationsPayload,
  ): Observable<AuditLocationListResponseDto> {
    return this.http.put<AuditLocationListResponseDto>(
      `${this.auditBaseUrl}/${auditId}/locations/`,
      payload,
    );
  }

  createAssignment(
    auditId: string,
    payload: CreateAuditAssignmentPayload,
  ): Observable<AuditAssignmentDto> {
    return this.http.post<AuditAssignmentDto>(
      `${this.auditBaseUrl}/${auditId}/assignments/`,
      payload,
    );
  }

  getAssignments(auditId: string): Observable<AuditAssignmentListResponseDto> {
    return this.http.get<AuditAssignmentListResponseDto>(
      `${this.auditBaseUrl}/${auditId}/assignments/`,
    );
  }

  getAnnualTargets(auditId: string): Observable<AuditAnnualTargetsResponseDto> {
    return this.http.get<AuditAnnualTargetsResponseDto>(`${this.auditBaseUrl}/${auditId}/annual-targets/`);
  }

  saveAnnualPlan(auditId: string, payload: SaveAuditAnnualPlanPayload): Observable<AuditAnnualTargetsResponseDto> {
    return this.http.put<AuditAnnualTargetsResponseDto>(`${this.auditBaseUrl}/${auditId}/annual-targets/`, payload);
  }

  replaceAssignment(
    auditId: string,
    payload: CreateAuditAssignmentPayload,
  ): Observable<ReplaceAuditAssignmentResponseDto> {
    return this.http.put<ReplaceAuditAssignmentResponseDto>(
      `${this.auditBaseUrl}/${auditId}/assignments/`,
      payload,
    );
  }

  getAuditCalendar(dateFrom: string, dateTo: string): Observable<AuditCalendarResponseDto> {
    return this.http.get<AuditCalendarResponseDto>(`${this.calendarBaseUrl}/`, {
      params: {
        date_from: dateFrom,
        date_to: dateTo,
      },
    });
  }

  startAuditExecutionPlan(planId: string): Observable<AuditExecutionPlanDto> {
    return this.http.post<AuditExecutionPlanDto>(
      `${this.executionPlansBaseUrl}/${planId}/start/`,
      {},
    ).pipe(tap(plan => {
      if (plan.execution_session) this.executionSessions.set(planId, plan.execution_session);
    }));
  }

  prepareAnnualExecution(auditId: string, locationId: string, requestId: string): Observable<AuditExecutionPlanDto> {
    return this.http.post<AuditExecutionPlanDto>(`${this.auditBaseUrl}/${auditId}/annual-executions/`, {
      location_id: locationId, request_id: requestId,
    });
  }

  getAuditExecution(planId: string): Observable<AuditExecutionDetailDto> {
    return this.http.get<AuditExecutionDetailDto>(
      `${this.executionPlansBaseUrl}/${planId}/execution/`,
    ).pipe(switchMap(execution => {
      if (execution.status === "in_progress" && execution.window_end_at && !this.executionSessions.has(planId)) {
        return this.startAuditExecutionPlan(planId).pipe(map(() => execution));
      }
      return of(execution);
    }));
  }

  autosaveAuditAnswer(
    planId: string,
    itemId: string,
    payload: AuditAnswerAutosavePayload,
  ): Observable<AuditAnswerDto> {
    return this.http.put<AuditAnswerDto>(
      `${this.executionPlansBaseUrl}/${planId}/answers/${itemId}/`,
      payload,
      this.sessionOptions(planId),
    );
  }

  completeAuditExecutionPlan(planId: string): Observable<AuditExecutionPlanDto> {
    return this.http.post<AuditExecutionPlanDto>(
      `${this.executionPlansBaseUrl}/${planId}/complete/`,
      {},
      this.sessionOptions(planId),
    );
  }

  getAuditUnlockInbox(): Observable<AuditUnlockInboxDto> {
    return this.http.get<AuditUnlockInboxDto>('/api/bhp/api/audit-unlock-requests/');
  }

  requestAuditUnlock(planId: string, reason: string): Observable<AuditUnlockRequestDto> {
    return this.http.post<AuditUnlockRequestDto>(
      `${this.executionPlansBaseUrl}/${planId}/unlock-request/`,
      { reason },
    );
  }

  decideAuditUnlock(requestId: string, decision: 'approve' | 'reject', note = ''): Observable<AuditUnlockRequestDto> {
    return this.http.post<AuditUnlockRequestDto>(
      `/api/bhp/api/audit-unlock-requests/${requestId}/decision/`,
      { decision, note },
    );
  }

  addAuditAnswerAttachment(
    planId: string,
    itemId: string,
    file: File,
  ): Observable<AuditAnswerAttachmentDto> {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<AuditAnswerAttachmentDto>(
      `${this.executionPlansBaseUrl}/${planId}/answers/${itemId}/attachments/`,
      formData,
      this.sessionOptions(planId),
    );
  }

  deleteAuditAnswerAttachment(
    planId: string,
    itemId: string,
    attachmentId: string,
  ): Observable<void> {
    return this.http.delete<void>(
      `${this.executionPlansBaseUrl}/${planId}/answers/${itemId}/attachments/${attachmentId}/`,
      this.sessionOptions(planId),
    );
  }

  deleteAudit(auditId: string): Observable<void> {
    return this.http
      .delete(`${this.auditBaseUrl}/${auditId}/`, { responseType: 'text' })
      .pipe(map(() => undefined));
  }
}
